/**
 * AddToCalendarMenu — unified "Add to Calendar" popover.
 *
 * Drop-in replacement for the broken blob-only download pattern that
 * shipped in `CareTab.jsx` before iteration 78. Used by Care Hub
 * appointments AND public Events for a consistent UX.
 *
 * Two paths offered:
 *   1. Google Calendar — quick-add via URL parameters. No auth, no
 *      blob, no download. Works on desktop + Android + iOS.
 *   2. Apple / Outlook (.ics) — fetches the server-rendered .ics blob
 *      and triggers a proper download, with iOS-specific UA fallback
 *      so Safari's "Open in Calendar" handler picks it up.
 *
 * Props:
 *   - title (string, required)
 *   - startsAt (ISO string, required)
 *   - endsAt (ISO string, optional) — when omitted, uses durationMin
 *   - durationMin (int, optional) — used only if endsAt is missing
 *   - location (string, optional)
 *   - description (string, optional)
 *   - icsUrl (string, required) — backend ICS endpoint, e.g.
 *       `/care/appointments/{id}/ics` or `/events/{id}/ics`
 *   - icsRequiresAuth (bool, default true) — if false, opens directly
 *       in a new tab instead of going through the authed axios client
 *   - icsFilename (string, optional) — desktop download filename
 *   - testId (string, optional) — root data-testid suffix
 *   - buttonProps (object, optional) — pass-through to the trigger Button
 */
import React from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { CalendarBlankIcon as CalendarBlank, FilePdfIcon as FilePdf } from "@phosphor-icons/react";

export default function AddToCalendarMenu({
  title,
  startsAt,
  endsAt,
  durationMin = 60,
  location = "",
  description = "",
  icsUrl,
  icsRequiresAuth = true,
  icsFilename,
  testId = "add-to-calendar",
  buttonProps = {},
}) {
  const start = new Date(startsAt);
  const end = endsAt
    ? new Date(endsAt)
    : new Date(start.getTime() + (durationMin || 60) * 60_000);

  // Format YYYYMMDDTHHmmssZ (Google Calendar TEMPLATE URL spec).
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");

  const googleUrl = (() => {
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: title,
      dates: `${fmt(start)}/${fmt(end)}`,
      details: description || "",
      location: location || "",
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  })();

  const isIOS = typeof navigator !== "undefined"
    && /iPad|iPhone|iPod/.test(navigator.userAgent)
    && !window.MSStream;

  const dl = async () => {
    try {
      let blob;
      if (icsRequiresAuth) {
        // Authed endpoint — go through axios so the bearer token is added.
        const r = await api.get(icsUrl, { responseType: "blob" });
        blob = new Blob([r.data], { type: "text/calendar;charset=utf-8" });
      } else {
        // Anonymous endpoint — direct fetch keeps things simple and
        // avoids dragging an unnecessary auth header.
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api${icsUrl}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        blob = new Blob([buf], { type: "text/calendar;charset=utf-8" });
      }
      const url = URL.createObjectURL(blob);
      const filename = icsFilename || `event-${Date.now()}.ics`;

      if (isIOS) {
        // iOS Safari ignores download="…" on blob URLs — opening in a
        // new tab triggers the system "Open in Calendar" prompt.
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return;
      }

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      // Defer cleanup so the browser actually starts the download
      // before the blob URL is revoked.
      setTimeout(() => {
        a.remove();
        URL.revokeObjectURL(url);
      }, 1_000);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't generate the .ics file");
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full text-xs"
          data-testid={testId}
          {...buttonProps}
        >
          Add to Calendar
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="end">
        <a
          href={googleUrl}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 px-3 py-2 text-[13px] rounded-md hover:bg-[#FAFAFA] cursor-pointer"
          data-testid={`${testId}-google`}
        >
          <CalendarBlank size={14} weight="duotone" className="text-blue-600" />
          <div className="flex-1 min-w-0">
            <p className="font-medium">Google Calendar</p>
            <p className="text-[10px] text-[#6B6B6B]">Opens calendar.google.com</p>
          </div>
        </a>
        <button
          onClick={dl}
          className="w-full flex items-center gap-2 px-3 py-2 text-[13px] rounded-md hover:bg-[#FAFAFA] cursor-pointer text-left"
          data-testid={`${testId}-ics`}
        >
          <FilePdf size={14} weight="duotone" className="text-ink" />
          <div className="flex-1 min-w-0">
            <p className="font-medium">Apple / Outlook (.ics)</p>
            <p className="text-[10px] text-[#6B6B6B]">Downloads a calendar file</p>
          </div>
        </button>
      </PopoverContent>
    </Popover>
  );
}
